import { ChangeDetectionStrategy, Component, ComponentRef, Inject, OnDestroy } from '@angular/core'
import { ActivatedRoute, convertToParamMap, NavigationEnd, ParamMap, Router } from '@angular/router'
import {
  EntityHubLayout,
  MessageModel,
  MultipleSectionsViewModel,
  FilterablePartial,
  LinkModel,
  GridContainerPartial,
  VideoPlayerCard,
  ContainerPartial,
  AnalyticsModel,
  FilterableGridPartial,
  SEOMetadata
} from '@flocasts/experience-service-types'
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  merge,
  Observable,
  of,
  pairwise,
  scan,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  takeUntil,
  tap,
  withLatestFrom
} from 'rxjs'
import { environment } from 'src/environments/environment'
import { LOGGER_SERVICE } from '../logger/logger.config'
import { LoggerService } from '../logger/logger.interface'
import { AdComponentInputs } from '../shared/ads/ad/ad-component.interface'
import { AdComponent } from '../shared/ads/ad/ad.component'
import {
  AD_SKIN_SIZE,
  AD_LEADERBOARD_LARGE_SIZE,
  AD_LEADERBOARD_SMALL_SIZE,
  AD_ADHESION_SIZE,
  AD_RECTANGLE_SMALL_SIZE,
  AD_INTERSTITIAL_SIZE
} from '../shared/ads/ads.utility'
import { AdTypes, AdTargeting, EqAdTargeting, AdConfig, HasInstance } from '../shared/ads/advertising-service/ad-config'
import { UpdateContentTargetingService } from '../shared/ads/advertising-service/update-content-targeting.service'
import { ExperimentationService } from '../shared/experimentation/experimentation.service'
import {
  isContainerPartial,
  isEntityHubLayout,
  isFilterableGridPartial,
  isFilterablePartial,
  isGridContainerPartial,
  isLinkModel,
  isMessageModel,
  isMultipleSectionsViewModel,
  isVideoPlayerCardPartial,
  notNullOrUndefined
} from '../shared/functions/type-guards'
import { getId, appendPaginatedItems, handleBrowserBackButton } from '../entity-hub/entity-hub.utility'
import { SegmentService } from '../shared/analytics/services/segment.service'
import { SegmentEvents } from '../shared/analytics/models/segment-events.model'
import { DEFAULT_OG_IMAGE_HEIGHT, DEFAULT_OG_IMAGE_WIDTH, PageMetaService } from '../libs/page-meta/page-meta.service'
import { WINDOW } from '../app.injections'
import { PlatformService } from '../singleton-services/platform.service'
import {
  EXP_FLOWRESTLING_EVENT_HUB_BRACKETS_ID,
  EXP_FLOWRESTLING_EVENT_HUB_BRACKETS_VARIATION
} from '../shared/experimentation/experiment.model'
import { PartialsDataService } from '../singleton-services/partials/partials-data.service'
import { EventHubDataService } from '../events/services/event-hub-data.service'
import { getEventHubTab, isSameEventHub, somethingInTheRouteChanged } from './event-hub.utility'
import { GeoBlockingService } from '../singleton-services/geo-blocking.service'
import { GeoIPResponseItem } from '../shared/geo-ip/geo-ip.service'
import type { EventPageProperties, SegmentTagV2 } from '../shared/analytics/models/segment-tag.model'

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './event-hub-smart.component.html'
})
export class EventHubSmartComponent implements OnDestroy {
  constructor(
    private readonly ar: ActivatedRoute,
    private readonly es: ExperimentationService,
    private readonly eventHubDataService: EventHubDataService,
    private readonly router: Router,
    private readonly updateAdTargeting: UpdateContentTargetingService,
    private readonly pageMetaService: PageMetaService,
    private readonly segmentService: SegmentService,
    private readonly platformService: PlatformService,
    private readonly partialsDataService: PartialsDataService,
    private readonly geoBlockingService: GeoBlockingService,
    @Inject(LOGGER_SERVICE) private readonly log: LoggerService,
    @Inject(WINDOW) private readonly window: Window
  ) {}
  private _id: number | null
  private _queryParams: ParamMap | null
  private _tab?: string | null
  private _tabs?: LinkModel[] | null

  /**
   * Determines if we make a partial request vs layout request.
   */
  private _isSameEventHub = false

  private readonly ngOnDestroy$ = new Subject<void>()

  /**
   * Triggered whenever content is loading in a tab.
   */
  public readonly isLoading$ = new BehaviorSubject<boolean>(false)

  // if layout service fails, then an error will show instead of the layout
  public readonly isLayoutError$ = new BehaviorSubject<boolean>(false)

  // if partial service fails, then an error will show only in the main content
  public readonly isPartialError$ = new BehaviorSubject<boolean>(false)

  private paginate$ = new Subject<LinkModel | undefined>()
  public handleNextPage(link: LinkModel | undefined) {
    this.paginate$.next(link)
  }

  private headerCta1ButtonClick$ = new Subject<LinkModel | undefined>()
  public handleHeaderCta1ButtonClick(link: LinkModel) {
    this.headerCta1ButtonClick$.next(link)
  }

  private tabChanged$ = new Subject<LinkModel | undefined>()
  public handleTabClicked(tab: LinkModel) {
    this.tabChanged$.next(tab)
  }

  // The browser back/forward selected tab
  public selectedTab$ = new Subject<LinkModel | undefined>()

  /**
   * This stream gets the child param from the parent route, which is actually quite difficult to do in Angular.
   */
  private routeUrl$ = this.router.events.pipe(
    tap(e => {
      const selectedTab = handleBrowserBackButton(e, this._tabs || [])
      if (!!selectedTab) this.selectedTab$.next(selectedTab)
    }),
    filter(e => e instanceof NavigationEnd),
    startWith(undefined),
    switchMap(_ => of(this.router.url) || of(undefined)),
    takeUntil(this.ngOnDestroy$),
    distinctUntilChanged()
  )

  readonly updatePageMeta = (seo: SEOMetadata) => {
    this.pageMetaService.setIsMetaClearable(true)
    this.pageMetaService.clearMeta()
    this.pageMetaService.setPageMeta({
      type: seo.ogType === 'website' ? seo.ogType : 'website',
      title: seo.title || '',
      description: seo.description || '',
      canonicalUrl: seo.canonicalUrl || '',
      siteName: null,
      twitterCard: seo.card === 'summary_large_image' ? seo.card : 'summary_large_image',
      image: {
        url: seo.imageUrl || '',
        height: Number(seo.imageHeight) || DEFAULT_OG_IMAGE_HEIGHT,
        width: Number(seo.imageWidth) || DEFAULT_OG_IMAGE_WIDTH
      }
    })
    this.pageMetaService.setIsMetaClearable(false)
  }

  /**
   * This secondary stream gets the video player, or any other future selected item that we want to highlight at the top of the page.
   * It is a separate stream from entityHubResponse$ so it does not conflict with the auto scroll to the top of the page.
   */
  public selectedItem$: Observable<VideoPlayerCard | undefined> = this.ar.queryParamMap.pipe(
    map(q => q?.get('playing')),
    switchMap(playing => {
      return !isNaN(Number(playing)) && Number(playing) > 0
        ? this.partialsDataService
            .getVideoPlayerCardPartial(Number(playing))
            .pipe(map(res => (isVideoPlayerCardPartial(res) ? res : undefined)))
        : of(undefined)
    }),
    tap(res => {
      if (res && !!res.seo) {
        this.updatePageMeta(res.seo)
      }
    }),
    map(res => {
      if (res) {
        return res.data
      }
      return
    }),
    takeUntil(this.ngOnDestroy$)
  )

  public shouldShowBrackets$ = this.es.isInVariation(
    EXP_FLOWRESTLING_EVENT_HUB_BRACKETS_ID,
    EXP_FLOWRESTLING_EVENT_HUB_BRACKETS_VARIATION
  )

  /**
   * One primary reactive stream to respond to most route change events and fire appropriate API requests
   */
  private entityHubResponse$: Observable<
    | EntityHubLayout
    | FilterablePartial
    | FilterableGridPartial
    | GridContainerPartial
    | MultipleSectionsViewModel
    | ContainerPartial
    | undefined
  > = combineLatest([
    this.shouldShowBrackets$,
    this.ar.queryParamMap,
    this.ar.paramMap,
    this.routeUrl$,
    this.tabChanged$.pipe(startWith(undefined))
  ]).pipe(
    // We must startWith because pairwise requires two values to emit, otherwise we never get data for the page.
    startWith([]),
    // We use pairwise bc the childParams$/router.event stream is chatty, causing several dupe api calls.
    pairwise(),
    // Now we can filter out dupe tab navigation events and prevent dupe api calls.
    filter(([[, oldQueryParams, oldParams, oldRouteUrl], [, queryParams, params, routeUrl]]) => {
      return somethingInTheRouteChanged(
        oldQueryParams || convertToParamMap({}),
        queryParams || convertToParamMap({}),
        oldParams || convertToParamMap({}),
        params || convertToParamMap({}),
        oldRouteUrl,
        routeUrl
      )
    }),
    tap(_ => this.isLoading$.next(true)),
    switchMap(([[, , oldParams], [isInBracketsVariation, queryParams, params, routeUrl, tabChanged]]) => {
      this._isSameEventHub = isSameEventHub(oldParams, params)
      this.scrollToTop(params, oldParams)
      // Reset pagination
      this.paginate$.next(undefined)
      // Reset tab
      this.tabChanged$.next(undefined)
      // Reset cta button
      this.headerCta1ButtonClick$.next(undefined)
      // Fetch new data
      return this.getEventHubExperience(params, queryParams, !!tabChanged, routeUrl, isInBracketsVariation)
    }),
    tap(_ => this.isLoading$.next(false)),
    shareReplay(1)
  )

  /**
   * Route logic for firing different api requests.
   * @param params The parent params always available: slug.
   * @param queryParams The query params, ex: facets.
   * @param tabChanged The tab changed event, so we know to fire the partial api request.
   * @param childParams The child params, ex: the tab, like videos, events, articles, etc.
   * @paginate paginate The pagination endpoint, ex: '/api/web/event-hub/1234/schedule/partial?limit=10&offset=20
   * @returns The api request stream.
   */
  private getEventHubExperience(
    params: ParamMap,
    queryParams: ParamMap,
    tabChanged: boolean,
    routeUrl: string | undefined,
    shouldShowBrackets: boolean = false
  ) {
    this._queryParams = queryParams
    this._tab = getEventHubTab(routeUrl)
    this._id = getId(params.get('slug'))
    if (!!this._id && !!this._tab && (tabChanged || this._isSameEventHub)) {
      return this.eventHubDataService
        .getEventHubPartial(this._id, this._tab, this._queryParams, shouldShowBrackets)
        .pipe(
          tap(partialRes => {
            !partialRes ? this.isPartialError$.next(true) : this.isPartialError$.next(false)
          })
        )
    }

    if (!!this._id) {
      return this.eventHubDataService
        .getEventHubLayoutWithCacheWrapper(this._id, this._tab, this._queryParams, shouldShowBrackets)
        .pipe(
          tap(layoutRes => {
            !layoutRes ? this.isLayoutError$.next(true) : this.isLayoutError$.next(false)
            if (!!layoutRes?.nav?.items) this._tabs = layoutRes?.nav.items.filter(isLinkModel)
          })
        )
    }

    return of(undefined)
  }

  // Emits on server and client so we get update timezone formatted strings
  public layout$: Observable<EntityHubLayout> = this.entityHubResponse$.pipe(
    switchMap(res => {
      if (isEntityHubLayout(res)) {
        return of(res)
      }
      return of(undefined)
    }),
    filter(notNullOrUndefined),
    shareReplay(1)
  )

  // Only emits when the event hub id changes
  private readonly distinctLayout$ = this.layout$.pipe(
    filter(notNullOrUndefined),
    filter((layout: EntityHubLayout) => !!layout.analytics),
    distinctUntilChanged((prev, curr) => prev.analytics.entityId === curr.analytics.entityId)
  )

  public geoBlockedIds$: Observable<ReadonlyArray<number>> = this.distinctLayout$.pipe(
    filter(_ => this.platformService.isBrowser),
    filter(res => isEntityHubLayout(res)),
    map(res => res?.metadata?.checkGeoRestriction && res.metadata.liveId),
    map(id => (!!id ? [id] : [])),
    switchMap(this.geoBlockingService.getGeoBlockedLiveEvents),
    map((geoIpResponseItem: GeoIPResponseItem[]) => geoIpResponseItem.map(res => res.id)),
    takeUntil(this.ngOnDestroy$),
    shareReplay(1)
  )

  public imageWithButtons$ = merge(
    this.distinctLayout$.pipe(
      map(res => res.secondaryContainer?.items.find(x => x.type === 'image-with-buttons')),
      filter(notNullOrUndefined)
    ),
    this.distinctLayout$.pipe(
      map(res => res.bottomContent?.items.find(x => x.type === 'image-with-buttons')),
      filter(notNullOrUndefined)
    )
  )

  /**
   * Displays the event description only on smaller screens. In the future could include related content.
   */
  public description$ = this.distinctLayout$.pipe(
    map(res => res.secondaryContainer?.items.find(x => x.type === 'expandable-text')),
    filter(notNullOrUndefined)
  )

  /**
   * This secondary stream's responsibility is to make paginated request and append items to the partial container.
   * If there is no paging triggered, it should just return the unmutated response.
   */
  private paginatedResponse$: Observable<
    | EntityHubLayout
    | MultipleSectionsViewModel
    | FilterablePartial
    | FilterableGridPartial
    | GridContainerPartial
    | ContainerPartial
    | undefined
  > = combineLatest([
    this.entityHubResponse$,
    this.paginate$.pipe(
      startWith(undefined),
      switchMap(link => {
        if (!!link?.url) {
          this.segmentService.trackEvent({
            name: SegmentEvents.INFINITE_SCROLL,
            properties: {
              page: link.analytics?.extraContext?.page
            }
          })
          return this.eventHubDataService.getEventHubPaginatedPartial(link.url)
        }
        return of(undefined)
      })
    )
  ]).pipe(
    scan((acc, [entityHubResponse, curr]) => {
      return appendPaginatedItems(acc, curr, entityHubResponse)
    }, undefined)
  )

  /**
   * If slug route params change,
   * we can assume we are navigating to event hub for the first time
   * and scroll up to the top.
   * @param newParams The most recent route params.
   * @param oldParams The previous route params.
   */
  private scrollToTop = (newParams: ParamMap, oldParams: ParamMap) => {
    if (this.platformService.isBrowser && getId(newParams?.get('slug')) !== getId(oldParams?.get('slug'))) {
      this.window.scroll(0, 0)
    }
  }

  public partial$: Observable<
    FilterablePartial | FilterableGridPartial | GridContainerPartial | ContainerPartial | undefined
  > = this.paginatedResponse$.pipe(
    map(res => {
      if (
        isFilterablePartial(res) ||
        isFilterableGridPartial(res) ||
        isGridContainerPartial(res) ||
        isContainerPartial(res)
      ) {
        return res
      }
      return
    }),
    shareReplay(1)
  )

  public message$: Observable<MessageModel | undefined> = this.entityHubResponse$.pipe(
    map(res => {
      if (res && isMultipleSectionsViewModel(res)) {
        return res.sections.find(isMessageModel)
      }
      return
    })
  )

  // Ads
  private readonly loadedAds$ = new BehaviorSubject<AdComponent[]>([])
  private readonly adConfigMap: Map<AdTypes, AdComponentInputs> = new Map()
  public readonly adLoadedOnChildView = (loadedAd: HasInstance<AdComponent>): void => {
    this.loadedAds$.next([...this.loadedAds$.getValue(), loadedAd.instance])
  }

  private readonly adTargeting$: Observable<AdTargeting> = this.distinctLayout$.pipe(
    map(entityHubResponse => {
      return {
        contentIds: entityHubResponse.adTargeting.aggregatedNodeIds,
        categorySlugs: entityHubResponse.adTargeting.aggregatedCategorySlugs,
        isPremium: entityHubResponse.adTargeting.isUserPremium
      }
    }),
    distinctUntilChanged(EqAdTargeting.equals),
    shareReplay(1)
  )

  private refreshAds$ = this.adTargeting$
    .pipe(
      withLatestFrom(this.loadedAds$),
      switchMap(args => {
        const [targeting, loadedAds] = args
        this.log.trace('Event Hubs: updating ad targeting for subsequent view')
        return this.updateAdTargeting.updateLoadedAdsTargeting(targeting, loadedAds).pipe(map(_ => args))
      }),
      switchMap(([targeting, loadedAds]) => {
        this.log.trace('Event Hubs: ad refresh triggered for subsequent view', {
          targeting
        })
        return this.updateAdTargeting.refreshLoadedAds(loadedAds)
      }),
      takeUntil(this.ngOnDestroy$)
    )
    .subscribe()

  public readonly AdComponent = import('../shared/ads/ad/ad.component').then(m => m.AdComponent)

  public readonly loadedAdsPush = (loadedAd: ComponentRef<AdComponent>): void => {
    this.loadedAds$.next([...this.loadedAds$.getValue(), loadedAd.instance])
  }

  public readonly adConfigs$: Observable<Map<AdTypes, AdComponentInputs>> = this.adTargeting$.pipe(
    map(targeting => {
      this.adConfigMap.set('skin', {
        config: {
          adConfig: AdConfig('events/post/atf', AD_SKIN_SIZE, 'event-hub-skin', targeting),
          additionalOpts: {
            deferClick: environment.advertising.preventClickTimeout
          }
        }
      })

      this.adConfigMap.set('leaderboard-large', {
        config: {
          adConfig: AdConfig('events/post/728x90_flex', AD_LEADERBOARD_LARGE_SIZE, 'event-hub-flex-large', targeting),
          additionalOpts: { deferClick: environment.advertising.preventClickTimeout }
        }
      })

      this.adConfigMap.set('leaderboard-small', {
        config: {
          adConfig: AdConfig('events/post/728x90_flex', AD_LEADERBOARD_SMALL_SIZE, 'event-hub-flex-small', targeting),
          additionalOpts: { deferClick: environment.advertising.preventClickTimeout }
        }
      })

      // Used on medium breakpoint as sticky adhesion ad
      this.adConfigMap.set('content-mobile', {
        config: {
          adConfig: AdConfig(
            'events/post/728x90_flex',
            AD_LEADERBOARD_SMALL_SIZE,
            'event-hub-flex-small-mobile',
            targeting
          ),
          additionalOpts: { maxRefreshTime: 30000 }
        }
      })

      this.adConfigMap.set('adhesion', {
        config: {
          adConfig: AdConfig('events/post/adhesion', AD_ADHESION_SIZE, 'event-hub-adhesion', targeting),
          additionalOpts: { maxRefreshTime: 30000 }
        }
      })

      this.adConfigMap.set('rectangle-mobile', {
        config: {
          adConfig: AdConfig('events/post/300x250_1', AD_RECTANGLE_SMALL_SIZE, 'event-hub-mobile-rectangle', targeting)
        }
      })

      this.adConfigMap.set('interstitial', {
        config: {
          adConfig: AdConfig('events/post/interstitial', AD_INTERSTITIAL_SIZE, 'event-interstitial', targeting)
        }
      })

      return this.adConfigMap
    })
  )

  /**
   * This stream is responsible for updating the page head with the appropriate meta tags when the layout changes.
   */
  private readonly updatePageHeadOnLayout$ = this.distinctLayout$
    .pipe(
      tap(res => {
        if (!!res.seo) this.updatePageMeta(res.seo)
      }),
      takeUntil(this.ngOnDestroy$)
    )
    .subscribe()

  /**
   * This stream is responsible for updating the page head with the appropriate meta tags when the partial changes.
   */
  private readonly updatePageHeadOnPartial$ = this.partial$
    .pipe(
      distinctUntilChanged((prev, curr) => prev?.seo?.title === curr?.seo?.title),
      tap(res => {
        if (!!res?.seo) this.updatePageMeta(res.seo)
      }),
      takeUntil(this.ngOnDestroy$)
    )
    .subscribe()

  private readonly trackSegmentPage = this.distinctLayout$
    .pipe(
      map(layout => layout.analytics),
      tap((analytics: AnalyticsModel) => {
        const segmentTag: SegmentTagV2 = {
          page_category: analytics.extraContext?.pageCategory || 'Event',
          subpage_category: analytics.extraContext?.subpageCategory,
          // @ts-ignore category is required on web, but nullable in the BFF
          category: analytics.entityType || null,
          node_type: analytics.entityType || undefined,
          autoPageCall: false
        }

        const segmentPageProperties: EventPageProperties = {
          _tag: 'event',
          // @ts-ignore node_id is required number on web, but nullable in the BFF
          node_id: analytics.entityId || undefined,
          content_tags: analytics.extraContext?.contentTags || undefined,
          is_premium: analytics.isPremium,
          live_id: analytics.extraContext?.liveId || undefined,
          live_event_status: analytics.extraContext?.liveEventStatus || undefined,
          utm_source: this._queryParams?.get('utm_source') || undefined
        }
        this.segmentService.pageV2(segmentTag, segmentPageProperties)
      }),
      takeUntil(this.ngOnDestroy$)
    )
    .subscribe()

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next()
    this.ngOnDestroy$.complete()
    this.loadedAds$.complete()
    this.tabChanged$.complete()
    this.paginate$.complete()
    this.isLoading$.complete()
    this.isLayoutError$.complete()
    this.isPartialError$.complete()
    this.headerCta1ButtonClick$.complete()
    this.selectedTab$.complete()
    this.pageMetaService.clearMeta()
  }
}
